import { useEffect, useMemo, useState } from "react";
import DynamicForm from "@view/common/DynamicForm";
import { DynamicFormSchema } from "@view/common/DynamicForm/DynamicFormContracts";
import styles from "./ConfirmationSlide.module.css";
import { confirmEmailForIngress, loginWithEmailAndPassword, sendVerificationCode } from "@view/common/Api/LoginApi";
import { ISignInProcessInitWithUrlDto } from "@presentation/controllersModels/ISignInProcessInitWithUrlDto";
import { useLoginDestiny } from "../useLoginDestiny";
import DynamicTextField from "../../Components/DynamicFields/DynamicTextField";
import { useNotificationModal } from "../../Components/NotificationModal/useNotificationModal";
import NotificationModal from "../../Components/NotificationModal";
import { Megaphone } from "../../Components/Icons";
import classNames from "classnames";
import min8LengthRegex from "@presentation/regexes/min8LengthRegex";
import LoginSubmitFieldWithResend from "../LoginSubmitFieldWithResend";
import { WrongLamp } from "@view/common/Components/Icons/WrongLamp";

export interface ILoginVerificationCodeFormValue {
  token: string;
}

export interface ILoginWithPasswordSlideProps {
  onSuccess: (sessionInfo: ISignInProcessInitWithUrlDto) => void;
  className?: string;
  email: string;
  password: string;
}

const ConfirmationSlide = (props: ILoginWithPasswordSlideProps) => {

  const [loading, setLoading] = useState(false);
  const [loadingCode, setLoadingCode] = useState(false);
  const [sliderPosition, setSliderPosition] = useState<boolean>(false);
  const [resetForm, setResetForm] = useState(false);
  const { notificationModalProps, openNotificationModal } = useNotificationModal();
  const loginDestiny = useLoginDestiny();

  const onLoginFail = () => {
    openNotificationModal({
      disableCloseButton: true,
      title: "Ocurrió un error",
      actionLabel: "Cerrar",
      image: () => <WrongLamp />,
      variant: "custom",
      description: `El código ingresado no es válido. Por favor verifica e intenta nuevamente.`
    });
  };

  const onUnspectedError = () => {
    openNotificationModal({
      disableCloseButton: true,
      title: "Ocurrió un error",
      actionLabel: "Cerrar",
      image: () => <WrongLamp />,
      variant: "custom",
      description: `El código ingresado no es válido. Por favor verifica e intenta nuevamente.`
    });
  };

  const codeFormSchema = useMemo(() => {
    const schema: DynamicFormSchema<ILoginVerificationCodeFormValue> = {
      submitLabel: "Validar",
      submitFieldColumns: 12,
      initValues: {
        token: "",
      },
      columns: 12,
      fields: [
        {
          columns: 12,
          label: "Código de verificación",
          name: "token",
          type: "string",
          attributes: {
            maxLength: 8,
            required: true,
            regexes: [{ expression: min8LengthRegex }],
          },
        },
      ],
      disableSubmitOnError: true,
    };
    return schema;
  }, []);

  const resendCode = async () => {
    setLoadingCode(true);
    await sendVerificationCode({
      email: props.email,
    })
    setResetForm(true);
    openNotificationModal({
      disableCloseButton: true,
      title: "Revisa tu correo electrónico",
      actionLabel: "Cerrar",
      image: () => <Megaphone />,
      variant: "custom",
      description: `Se ha generado un nuevo código, por favor ingrésalo en el campo "Código de verificación".`
    });
    setLoadingCode(false);
  };

  const onSubmit = (value: ILoginVerificationCodeFormValue, anyError: boolean) => {
    if (anyError) return;
    callApi(value.token);
  };

  const callApi = async (token: string) => {

    setLoading(true);

    try {
      const response = await confirmEmailForIngress({
        email: props.email,
        token
      })

      if (!response.isSuccess) {
        setLoading(false);
        if (response.status === 401) {
          onLoginFail();
        } else {
          onUnspectedError();
        }
        return;
      }

      if (props.email && props.password) {
        const loginResponse = await loginWithEmailAndPassword({
          email: props.email,
          password: props.password,
          destiny: loginDestiny,
        });

        if (!loginResponse.isSuccess) {
          setLoading(false);
          if (response.status === 401) {
            onLoginFail();
          } else {
            onUnspectedError();
          }
          return;
        }

        props.onSuccess(loginResponse.body);
      }

    }
    catch (e) {
      setLoading(false);
      onUnspectedError();
      return;
    }

  };

  useEffect(() => { if (resetForm === true) setResetForm(false); }, [resetForm]);

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <h1>Código de verificación</h1>
        <p className={styles.message}>
          Te hemos enviado un código a tu correo “<span style={{ color: "var(--color-purple-main)" }}>{props.email}</span>”
          por favor ingrésalo a continuación para poder continuar con tu proceso.
          <br /><br />{`Búscalo en la "Bandeja de entrada", "No deseados" o "Spam".`}
        </p>
      </div>
      <div className={styles.form}>
        <DynamicForm
          reset={resetForm}
          className={classNames({ [styles.hidden]: sliderPosition }, styles.root)}
          disabled={loading}
          loading={loading}
          onSubmit={onSubmit as any}
          schema={codeFormSchema}
          context={{
            fieldsMap: {
              "string": DynamicTextField,
            },
            SubmitField: (x) => (
              <LoginSubmitFieldWithResend loadingResend={loadingCode} loading={loading} {...x} label="Validar" onResendClick={resendCode} />
            ),
            requiredIndicator: true
          }}
        />
      </div>
      <NotificationModal loading={loadingCode} {...notificationModalProps} />
    </div>
  );

};

export default ConfirmationSlide;
